import { Module, ActionTree, MutationTree, GetterTree, Dispatch } from "vuex"
import { RootState } from "@/store/RootStore"
import { make } from "vuex-pathify"
import { StoreUtils } from "@/utils/StoreUtils"
import services from "@/services/Services"
import { CreateGuestInvitePayload, GuestInviteListItem, InviteStatus, OverlappingInvitationInfo, UpdateGuestInvitePayload } from "@/models/GuestInvite"
import { AxiosError } from "axios"
import { ApiError } from "rp-components"

const namespaced: boolean = true
const OVERLAPPING_GUEST_INVITATION_ERROR_TYPE = "OVERLAPPING_GUEST_INVITATION"

interface ApiErrorWithOverlap extends ApiError {
    overlappingInvitations?: OverlappingInvitationInfo[]
}

function parseOverlappingInvitations(apiError?: ApiErrorWithOverlap): OverlappingInvitationInfo[] {
    if (apiError?.errorType !== OVERLAPPING_GUEST_INVITATION_ERROR_TYPE) {
        return []
    }
    return apiError.overlappingInvitations ?? []
}

async function withOverlapGuard(dispatch: Dispatch, action: () => Promise<{ status: InviteStatus }>): Promise<void> {
    await StoreUtils.withLoading(
        dispatch,
        async () => {
            dispatch("setOverlappingInvitations", [])
            const result = await action()
            dispatch("setSubmitResult", result.status)
            dispatch("setSubmitSuccess", true)
        },
        async (err: unknown) => {
            const axiosError = err as AxiosError | undefined
            const apiError = axiosError?.response?.data as ApiErrorWithOverlap | undefined
            const overlappingInvitations = parseOverlappingInvitations(apiError)
            if (overlappingInvitations.length === 0) {
                return false
            }
            dispatch("setOverlappingInvitations", overlappingInvitations)
            return true
        }
    )
}

//===================================
// State
//===================================

export interface GuestInviteState {
    submitSuccess: boolean
    submitResult: InviteStatus | null
    overlappingInvitations: OverlappingInvitationInfo[]
    myInvites: GuestInviteListItem[]
    myInvitesNextLink: string | undefined
    myInvitesPageSize: number
}

function defaultState(): GuestInviteState {
    return {
        submitSuccess: false,
        submitResult: null,
        overlappingInvitations: [],
        myInvites: [],
        myInvitesNextLink: undefined,
        myInvitesPageSize: 10
    }
}

export const state: GuestInviteState = defaultState()

//===================================
// Getters
//===================================
export const getters: GetterTree<GuestInviteState, RootState> = {
    ...make.getters(state)
}

//===================================
// Actions
//===================================
export const actions: ActionTree<GuestInviteState, RootState> = {
    ...make.actions(state),

    resetState({ commit }) {
        commit("resetState")
    },

    async sendInvite({ dispatch }, payload: CreateGuestInvitePayload) {
        await withOverlapGuard(dispatch, () => services.guestInvites.createInvite(payload))
    },

    async updateInvite({ dispatch }, { id, payload }: { id: string; payload: UpdateGuestInvitePayload }) {
        await withOverlapGuard(dispatch, () => services.guestInvites.updateInvite(id, payload))
    },

    async revokeInvite({ dispatch }, id: string) {
        await StoreUtils.withLoading(dispatch, async () => {
            await services.guestInvites.revokeInvite(id)
            await dispatch("setAlert", "The access has been successfully revoked.", { root: true })
            await dispatch("loadMyInvites")
        })
    },

    async resendInvite({ dispatch }, id: string) {
        await StoreUtils.withLoading(dispatch, async () => {
            await services.guestInvites.resendInvite(id)
            await dispatch("setAlert", "The invitation has been successfully resent.", { root: true })
            await dispatch("loadMyInvites")
        })
    },

    async loadMyInvites({ dispatch, state }) {
        await StoreUtils.withLoading(dispatch, async () => {
            const result = await services.guestInvites.getMyInvites(state.myInvitesPageSize)
            dispatch("setMyInvites", result.items ?? [])
            dispatch("setMyInvitesNextLink", result.next ?? undefined)
        })
    },

    async loadMyInvitesNextPage({ dispatch, state }) {
        const nextLink = state.myInvitesNextLink
        if (!nextLink) return
        await StoreUtils.withLoading(dispatch, async () => {
            const result = await services.guestInvites.getMyInvitesNextPage(nextLink)
            dispatch("setMyInvites", [...state.myInvites, ...(result.items ?? [])])
            dispatch("setMyInvitesNextLink", result.next ?? undefined)
        })
    }
}

//===================================
// Mutations
//===================================
const mutations: MutationTree<GuestInviteState> = {
    ...make.mutations(state),

    resetState(currentState: GuestInviteState) {
        Object.assign(currentState, defaultState())
    }
}

//===================================
// Module
//===================================
export const guestInvite: Module<GuestInviteState, RootState> = {
    namespaced,
    state,
    getters,
    actions,
    mutations
}
